import { useEffect, useRef } from "react";

import {
  TaxonomyIngestionSessionDocument,
  type TaxonomyIngestionSessionSubscription,
} from "@/graphql/generated";
import { getSubscriptionClient } from "@/graphql/subscriptionClient";

export interface UseTaxonomyIngestionSessionSubscriptionOptions {
  runId?: string | null;
  enabled?: boolean;
  onEvent?: (
    event: TaxonomyIngestionSessionSubscription["taxonomyIngestionSession"],
  ) => void;
  onError?: (error: unknown) => void;
}

export function useTaxonomyIngestionSessionSubscription({
  runId,
  enabled = true,
  onEvent,
  onError,
}: UseTaxonomyIngestionSessionSubscriptionOptions): void {
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);

  onEventRef.current = onEvent;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!runId || !enabled) return;

    const client = getSubscriptionClient();
    const unsubscribe = client.subscribe(
      {
        query: TaxonomyIngestionSessionDocument,
        variables: { runId },
      },
      {
        next: (result) => {
          const data = result.data as
            | TaxonomyIngestionSessionSubscription
            | undefined;
          if (data?.taxonomyIngestionSession) {
            onEventRef.current?.(data.taxonomyIngestionSession);
          }
        },
        error: (error) => {
          onErrorRef.current?.(error);
        },
        complete: () => {},
      },
    );

    return () => {
      unsubscribe();
    };
  }, [enabled, runId]);
}
